import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as OpenSVG } from "go-core/images/svg/actions/link.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import FormatResourceStatus from "../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { ReactComponent as GoposLogoSVG } from "../../../../../images/svg/organization/gopos_logo.svg";
import { IntercomConversationApi, OrganizationPartnerInfoApi } from "../../../../../services/Api/Admin/adminTypes";
import { OrganizationAlert } from "../../../../../services/Api/Organization/organizationTypes";
import { OrganizationApi, OrganizationProductPerCategoryApi, UserRole } from "../../../../../services/Api/types";
import { selectPartner } from "../../../../../services/partner/selectors";
import { selectUser } from "../../../../../services/users/selectors";
import FormatSubscriptionLicenseType from "../../FormatSubscriptionLicenseType/FormatSubscriptionLicenseType";
import { getOrganizationAlerts } from "../../Organization/utils";
import OrganizationCompanyInfoCard from "./OrganizationCompanyInfoCard";
import OrganizationAlertsCard from "./components/OrganizationAlertsCard";
import OrganizationAlertsModal from "./components/OrganizationAlertsModal";
import OrganizationContactInfoCard from "./components/OrganizationContactInfoCard";
import OrganizationFeaturesCard from "./components/OrganizationFeaturesCard";
import OrganizationFeaturesEditModal from "./components/OrganizationFeaturesEditModal";
import OrganizationFinancialCard from "./components/OrganizationFinancialCard";
import OrganizationInfoCard from "./components/OrganizationInfoCard";
import OrganizationIntegrationsCard from "./components/OrganizationIntegrationsCard";
import OrganizationLicenseCard from "./components/OrganizationLicenseCard";
import OrganizationMergingModal from "./components/OrganizationMergingModal";
import OrganizationPartnersInfoCard from "./components/OrganizationPartnersInfoCard";
import OrganizationPreviewTabNavigation from "./components/OrganizationPreviewTabNavigation";
import OrganizationProductsCard from "./components/OrganizationProductsCard";
import OrganizationSupportCard from "./components/OrganizationSupportCard";
import OrganizationUpdateBillingDateModalForm from "./components/OrganizationUpdateBillingDateModalForm";
import OrganizationUpdateTypeModal from "./components/OrganizationUpdateTypeModal";
import { OrganizationPreviewRequests } from "./services/types";

interface Props {
	organization: OrganizationApi;
	url: string;
	refresh: () => Promise<void>;
	requests: OrganizationPreviewRequests;
	context: string;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const OrganizationPreview = ({ organization, url, context, refresh, requests, customFieldsConfig }: Props) => {
	const { t } = useTranslation("admin");
	const partner = useSelector(selectPartner);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [showOrganizationMergingModal, setShowOrganizationMergingModal] = useState<boolean>(false);
	const [showFeaturesEditModal, setShowFeaturesEditModal] = useState<boolean>(false);
	const [partners, setPartners] = useState<OrganizationPartnerInfoApi[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const [conversations, setConversations] = useState<IntercomConversationApi[]>([]);
	const [showAlertsModal, setShowAlertsModal] = useState<boolean>(false);
	const user = useSelector(selectUser);
	const alerts: OrganizationAlert[] = getOrganizationAlerts(
		organization.license_expires_at,
		organization.license_type,
		organization.payment_date_due,
		t,
		true,
		organization.abused_access,
		organization.outdated_terminals_count,
		organization.test_version_terminals_count,
		organization.timezone
	);
	const organizationTypeIsDemo = organization.type === "DEMO";
	const isMobile = useWindowSize().isMobile;
	const [showUpdateBillingDateModal, setShowUpdateBillingDateModal] = useState(false);
	const [products, setProducts] = useState<OrganizationProductPerCategoryApi[]>([]);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [showChangeOrganizationType, setShowChangeOrganizationType] = useState(false);
	const roles = user.roles ? user.roles : [];

	useEffect(() => {
		handleInit();
	}, []);

	const handleInit = async () => {
		if (getCardTypes().includes("PARTNERS")) await fetchPartners();
		if (getCardTypes().includes("PRODUCTS")) await fetchProducts();
		await fetchSupportConversations();
	};

	const getCardTypes = () => {
		if (context === "ADMIN") {
			return [
				"APPLICATIONS",
				"SUBSCRIPTIONS",
				"USERS",
				"SUPPORT",
				"PARTNERS",
				"TERMINALS",
				"PRODUCTS",
				"FEATURES",
				"INVOICES",
			];
		}
		if (context === "PARTNER") {
			return [
				"APPLICATIONS",
				"SUBSCRIPTIONS",
				"USERS",
				"PARTNERS",
				"TERMINALS",
				"PRODUCTS",
				"FEATURES",
				"INVOICES",
			];
		}
		if (context === "SUPPORT") {
			return [
				"APPLICATIONS",
				"SUBSCRIPTIONS",
				"USERS",
				"SUPPORT",
				"TERMINALS",
				"PRODUCTS",
				"FEATURES",
				"INVOICES",
			];
		}
		return [];
	};

	const areRolesAllowed = (role: UserRole[]) => role.some((r) => roles.includes(r));
	const areContextAllowed = (contexts: string[]) => contexts.some((x) => context === x);
	const areAllowed = (contexts: Record<string, UserRole[]>) => {
		const selectContext = contexts[context];
		if (!selectContext) return false;
		if (selectContext.length === 0) return true;
		return areRolesAllowed(selectContext);
	};

	const fetchPartners = async () => {
		if (!requests.partners) return;

		try {
			const res = await requests.partners(organization.id);
			setPartners(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchSupportConversations = async () => {
		if (!requests.intercomConversations) return;

		setLoading(true);
		try {
			const res = await requests.intercomConversations(organization.id);
			setConversations(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const autoSubscription = async () => {
		if (!requests.subscriptionAuto) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.subscriptionAuto(organization.id);
			await refresh();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const manualSubscription = async () => {
		if (!requests.subscriptionManual) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.subscriptionManual(organization.id);
			await refresh();
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const getEditOrganizationLinkForAdmin = () => {
		if (partner?.id) return `/partnership/${partner?.id}/organizations/${organization.id}/edit`;
		return `/admin/organizations/${organization.id}/edit`;
	};

	const onBlockLicense = async (item: OrganizationApi) => {
		if (!requests.subscriptionBlock) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.subscriptionBlock(item.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const onUnblockLicense = async (item: OrganizationApi) => {
		if (!requests.subscriptionUnblock) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.subscriptionUnblock(item.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const downloadFromChargebee = async (item: OrganizationApi) => {
		if (!requests.downloadFromChargebee) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.downloadFromChargebee(item.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const uploadToChargebee = async (item: OrganizationApi) => {
		if (!requests.uploadToChargebee) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.uploadToChargebee(item.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const updateCF = async (item: OrganizationApi) => {
		if (!requests.updateCF) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await requests.updateCF(item.id);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			await refresh();
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const fetchProducts = async () => {
		if (!requests.getProducts) return;

		setLoadingProducts(true);
		try {
			const res = await requests.getProducts(organization.id, {
				include: "products",
			});
			setProducts(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingProducts(false);
	};

	const renderTitle = () => (
		<>
			{organization.name}
			<FormatResourceStatus status={organization.status} />
			<FormatSubscriptionLicenseType license_type={organization.license_type} />
			{organizationTypeIsDemo && <span className="badge bg-primary">Demo</span>}
			<Link to={`/${organization.id}`}>
				<OpenSVG />
			</Link>
		</>
	);

	const renderSubtitle = () => (
		<ul>
			<li>
				{t("common.word.category")}: {t(`enums.organization_category.${organization.category}`)}
			</li>
		</ul>
	);

	const getEditProductsPath = () => {
		if (partner?.id) {
			return `/partnership/${partner.id}/organizations/${organization.id}/edit_products`;
		}
		return `/admin/organizations/${organization.id}/edit_products`;
	};

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.edit", { ns: "lib" }),
			variant: "light",
			path: getEditOrganizationLinkForAdmin(),
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			title: t("modules.organization.action.update_accesses.title"),
			path: `/admin/organizations/${organization.id}/accesses`,
			dropdown: true,
		},
		{
			hide: !areAllowed({
				ADMIN: [UserRole.OFFICE, UserRole.ADMIN],
				PARTNER: [UserRole.SALE, UserRole.OFFICE, UserRole.ADMIN, UserRole.SUPPORT],
			}),
			path: getEditProductsPath(),
			title: t("modules.organization.action.update_products.title"),
			dropdown: true,
		},
		{
			title: t("modules.organization.action.change_partner.title"),
			path: `/admin/organizations/${organization.id}/partner`,
			dropdown: true,
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
		},
		{
			hide: !(areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }) && organization.license_type === "MANUAL"),
			title: t("modules.organization.action.auto_subscription.title"),
			action: autoSubscription,
			dropdown: true,
		},
		{
			hide: !(areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }) && organization.license_type !== "MANUAL"),
			title: t("modules.organization.action.manual_subscription.title"),
			action: manualSubscription,
			dropdown: true,
		},
		{
			hide: !(areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }) && organization.status === "ENABLED"),
			title: t("common.action.block_license"),
			action: () => onBlockLicense(organization),
			dropdown: true,
		},
		{
			hide: !(areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }) && organization.status === "BLOCKED"),
			title: t("common.action.unblock_license"),
			action: () => onUnblockLicense(organization),
			dropdown: true,
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			title: t("modules.organization.action.merge_organization.title"),
			action: () => setShowOrganizationMergingModal(true),
			dropdown: true,
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			title: t("common.word.logs", { ns: "lib" }),
			action: () => setShowLogs(!showLogs),
			dropdown: true,
		},
		{
			hide: !areAllowed({
				ADMIN: [UserRole.OFFICE, UserRole.ADMIN, UserRole.SALE, UserRole.SUPPORT],
				SUPPORT: [UserRole.OFFICE, UserRole.ADMIN, UserRole.SALE, UserRole.SUPPORT],
			}),
			dropdown: true,
			action: () => downloadFromChargebee(organization),
			title: t("modules.organization.action.download_from_chargebee.title"),
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			dropdown: true,
			action: () => uploadToChargebee(organization),
			title: t("modules.organization.action.upload_to_chargebee.title"),
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			action: () => updateCF(organization),
			dropdown: true,
			title: t("modules.organization.action.update_cf.title"),
		},
		{
			action: () => setShowChangeOrganizationType(true),
			title: t("modules.organization.action.change_organization_type.title"),
			dropdown: true,
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
		},
		{
			hide: !areAllowed({ ADMIN: [UserRole.OFFICE, UserRole.ADMIN] }),
			action: () => setShowUpdateBillingDateModal(true),
			title: t("modules.organization.action.update_billing_date.title"),
			dropdown: true,
		},
	];

	const mobileActions: MobileActionProps[] = buttons.map((button) => {
		return {
			title: button.title || "",
			path: button.path,
			hide: button.hide,
			action: button.action,
		};
	});

	return (
		<div className={"content-preview"}>
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				subtitle={renderSubtitle()}
				avatar={
					<div className={"header-ellipse"}>
						<span className={"header"}>
							<GoposLogoSVG />
						</span>
					</div>
				}
				tags={organization?.tags}
				buttons={isMobile ? [] : buttons}
			/>
			<div className={"content-preview-body"}>
				<div className={"row"}>
					<div className="col-lg-4">
						{areContextAllowed(["ADMIN", "PARTNER", "SUPPORT"]) && (
							<>
								{alerts.length > 0 && (
									<OrganizationAlertsCard alerts={alerts} setShowAlertsModal={setShowAlertsModal} />
								)}
							</>
						)}
						<OrganizationLicenseCard
							url={url}
							organization={organization}
							requests={requests}
							refresh={refresh}
							areAllowed={areAllowed}
						/>
						<OrganizationFeaturesCard
							organization={organization}
							loading={false}
							areAllowed={areAllowed}
							url={url}
							onEdit={() => setShowFeaturesEditModal(true)}
						/>
						{areContextAllowed(["ADMIN", "PARTNER", "SUPPORT"]) && (
							<>
								{products.length > 0 && (
									<OrganizationProductsCard
										products={products}
										loading={loadingProducts}
										currency={organization.currency ?? ""}
										url={url}
										areAllowed={areAllowed}
									/>
								)}
							</>
						)}
						<OrganizationInfoCard
							organization={organization}
							isAdminMode={areRolesAllowed([
								UserRole.ADMIN,
								UserRole.OFFICE,
								UserRole.SUPPORT,
								UserRole.SALE,
							])}
							customFieldsConfig={customFieldsConfig}
						/>
						{areContextAllowed(["ADMIN", "PARTNER"]) && (
							<OrganizationContactInfoCard
								place={organization.place}
								company_phone_number={organization.company_phone_number}
							/>
						)}
						{areContextAllowed(["SUPPORT", "ADMIN"]) && (
							<OrganizationSupportCard conversation={conversations[0]} loading={loading} />
						)}
						<OrganizationCompanyInfoCard
							company_build_nr={organization?.company_build_nr}
							company_city={organization?.company_city}
							company_email={organization?.company_email}
							company_phone_number={organization?.company_phone_number}
							company_street={organization?.company_street}
							company_zip_code={organization?.company_zip_code}
							company_tax_id_no={organization?.company_tax_id_no}
						/>
						{areContextAllowed(["ADMIN"]) && organization?.integrations?.length && (
							<OrganizationIntegrationsCard integrations={organization.integrations} />
						)}
						{areContextAllowed(["ADMIN", "PARTNER"]) && (
							<OrganizationFinancialCard organization={organization} />
						)}
						{areContextAllowed(["ADMIN", "SUPPORT"]) && partners?.length > 0 && (
							<OrganizationPartnersInfoCard
								organizationId={organization.id}
								partners={partners?.filter((_, index) => index < 2)}
							/>
						)}
					</div>
					<OrganizationPreviewTabNavigation
						organization={organization}
						url={url}
						conversations={conversations}
						requests={requests}
						refresh={refresh}
						areAllowed={areAllowed}
						partners={partners}
						loading={loading}
						getCardTypes={getCardTypes}
						products={products}
						loadingProducts={loadingProducts}
						context={context}
					/>
				</div>
			</div>
			{showOrganizationMergingModal && (
				<OrganizationMergingModal
					onHide={() => setShowOrganizationMergingModal(false)}
					isShown={showOrganizationMergingModal}
					organization={organization}
					fetchOrganization={() => requests.get(organization.id)}
				/>
			)}
			{showFeaturesEditModal && (
				<OrganizationFeaturesEditModal
					requests={requests}
					organization={organization}
					isShown={showFeaturesEditModal}
					onHide={() => setShowFeaturesEditModal(false)}
					context={context}
					onSuccess={() => setShowFeaturesEditModal(false)}
					areAllowed={areAllowed}
				/>
			)}
			{showAlertsModal && (
				<OrganizationAlertsModal
					isShown={showAlertsModal}
					onHide={() => setShowAlertsModal(false)}
					alerts={alerts}
				/>
			)}
			{showLogs && (
				<MessageEventModal
					path={`/logs/message_events`}
					resourceId={organization.company_id}
					resourceType={"ORGANIZATION"}
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			{showUpdateBillingDateModal && requests.getBillingCustomer && requests.updateBillingDate && (
				<OrganizationUpdateBillingDateModalForm
					show={showUpdateBillingDateModal}
					onHide={() => setShowUpdateBillingDateModal(false)}
					handleRefresh={refresh}
					getBillingCustomer={requests.getBillingCustomer}
					updateBillingDate={requests.updateBillingDate}
					organizationId={organization.id}
				/>
			)}
			{showChangeOrganizationType && (
				<OrganizationUpdateTypeModal
					onHide={() => setShowChangeOrganizationType(false)}
					changeOrganizationType={requests.changeOrganizationType}
					refresh={refresh}
					organization={organization}
				/>
			)}
		</div>
	);
};
export default OrganizationPreview;

// t(`enums.organization.card_type.APPLICATIONS`);
// t(`enums.organization.card_type.SUBSCRIPTIONS`);
// t(`enums.organization.card_type.PARTNERS`);
// t(`enums.organization.card_type.USERS`);
// t(`enums.organization.card_type.SUPPORT`);
// t(`enums.organization.card_type.TERMINALS`);
// t(`enums.organization.card_type.PRODUCTS`);
// t(`enums.organization.card_type.FEATURES`);
// t(`enums.organization.card_type.INVOICES`);
